import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  InputAdornment
} from '@mui/material';
import { Edit, Delete, CheckCircle, Cancel, AdminPanelSettings, PersonAdd, ContentCopy } from '@mui/icons-material';
import { getUsers, createUser, updateUser, deleteUser } from '@/services/api/userService';
import { formatDateOnly, getDaysUntilExpiry } from '@/utils/formatDate';
import type { User, UserCreate, UserUpdate, LicenseType, Product, UserRole } from '@types';
import axios from 'axios';
import { getToken } from '@/utils/storage';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ninshousystem-api-361153976392.asia-northeast1.run.app';

interface ValidationError {
  type: string;
  loc: (string | number)[];
  msg: string;
  input?: unknown;
  ctx?: Record<string, unknown>;
}

interface ApiErrorResponse {
  detail?: string | ValidationError[];
}

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined;
    if (data?.detail) {
      // Pydanticバリデーションエラー（配列）の場合
      if (Array.isArray(data.detail)) {
        return data.detail.map((e) => e.msg).join(', ');
      }
      // 通常のエラーメッセージ（文字列）の場合
      return data.detail;
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

// ライセンスタイプの表示名
const LICENSE_TYPE_LABELS: Record<LicenseType, string> = {
  BASIC: 'Basic',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise'
};


export const UsersPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // ダイアログ状態
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // フォーム状態
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'user' as UserRole,
    tenant_id: undefined as number | undefined,
    license_valid_until: '',
    // マルチプロダクト対応
    product_code: '',
    plan_code: '',
    // Gambit用フィールド（後方互換性）
    license_type: 'PRO' as LicenseType,
    max_devices: 1,
    subscription_ends_at: ''
  });

  // 選択中のプロダクトのプラン一覧を取得
  const selectedProduct = products.find(p => p.code === formData.product_code);
  const availablePlans = selectedProduct?.plans || [];

  useEffect(() => {
    fetchUsersList();
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API_BASE}/products`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchUsersList = async () => {
    try {
      setLoading(true);
      const data = await getUsers();
      setUsers(data);
      setError('');
    } catch (err) {
      setError('ユーザー一覧の取得に失敗しました');
      console.error('Fetch users error:', err);
    } finally {
      setLoading(false);
    }
  };

  // datetime-local形式をISO形式に変換
  const toISOString = (dateTimeLocal: string): string | undefined => {
    if (!dateTimeLocal) return undefined;
    // datetime-local形式 (YYYY-MM-DDTHH:mm) をISO形式に変換
    const date = new Date(dateTimeLocal);
    return isNaN(date.getTime()) ? undefined : date.toISOString();
  };

  const handleCreateUser = async () => {
    try {
      const userData: UserCreate = {
        email: formData.email,
        password: formData.password || undefined,  // 空欄ならundefined（バックエンドで自動生成）
        full_name: formData.full_name || undefined,
        role: formData.role,
        // マルチプロダクト対応（一般ユーザーのみ）
        ...(formData.role === 'user' && formData.product_code && {
          product_code: formData.product_code,
          plan_code: formData.plan_code || 'free',
        }),
        // 汎用フィールド（一般ユーザーのみ）
        ...(formData.role === 'user' && {
          max_devices: formData.max_devices,
          subscription_ends_at: toISOString(formData.subscription_ends_at)
        })
        // license_typeは送信しない（バックエンドでデフォルト処理）
      };
      await createUser(userData);
      setSuccessMessage('ユーザーを作成しました（メール送信済み）');
      setCreateDialogOpen(false);
      resetForm();
      fetchUsersList();
    } catch (err) {
      setError(getErrorMessage(err) || 'ユーザーの作成に失敗しました');
      console.error('Create user error:', err);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      const userData: UserUpdate = {
        full_name: formData.full_name || undefined,
        role: formData.role,
        license_valid_until: toISOString(formData.license_valid_until),
        // 汎用フィールド
        max_devices: formData.max_devices,
        subscription_ends_at: toISOString(formData.subscription_ends_at)
      };
      await updateUser(selectedUser.id, userData);
      setSuccessMessage('ユーザー情報を更新しました');
      setEditDialogOpen(false);
      resetForm();
      fetchUsersList();
    } catch (err) {
      setError(getErrorMessage(err) || 'ユーザーの更新に失敗しました');
      console.error('Update user error:', err);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await deleteUser(selectedUser.id);
      setSuccessMessage('ユーザーを削除しました');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsersList();
    } catch (err) {
      setError(getErrorMessage(err) || 'ユーザーの削除に失敗しました');
      console.error('Delete user error:', err);
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name || '',
      role: user.role,
      tenant_id: user.tenant_id ?? undefined,
      license_valid_until: user.license_valid_until || '',
      // マルチプロダクト（編集時は変更不可なので空）
      product_code: '',
      plan_code: '',
      // Gambit用フィールド
      license_type: user.license_type || 'BASIC',
      max_devices: user.max_devices || 1,
      subscription_ends_at: user.subscription_ends_at || ''
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'user',
      tenant_id: undefined,
      license_valid_until: '',
      product_code: '',
      plan_code: '',
      license_type: 'PRO',
      max_devices: 1,
      subscription_ends_at: ''
    });
    setSelectedUser(null);
  };

  // プランコードをGambit互換のlicense_typeに変換
  const mapPlanToLicenseType = (planCode: string): LicenseType => {
    const upperPlan = planCode.toUpperCase();
    // 有効なlicense_typeのみ返す
    if (upperPlan === 'BASIC' || upperPlan === 'PRO' || upperPlan === 'ENTERPRISE') {
      return upperPlan as LicenseType;
    }
    // それ以外はプラン名から推定（free→BASIC, starter→BASIC, etc.）
    if (planCode.includes('enterprise') || planCode.includes('team')) {
      return 'ENTERPRISE';
    }
    if (planCode.includes('pro') || planCode.includes('premium') || planCode.includes('plus')) {
      return 'PRO';
    }
    return 'BASIC'; // デフォルト
  };

  // プロダクト選択時にプランをリセット
  const handleProductChange = (productCode: string) => {
    const product = products.find(p => p.code === productCode);
    const plans = product?.plans || [];
    const firstPlan = plans[0];
    const defaultPlan = firstPlan?.code || '';
    const defaultMaxDevices = firstPlan?.max_devices || 1;
    const licenseType = mapPlanToLicenseType(defaultPlan);
    setFormData({
      ...formData,
      product_code: productCode,
      plan_code: defaultPlan,
      max_devices: defaultMaxDevices,
      license_type: licenseType
    });
  };

  // プラン選択時にmax_devicesとlicense_typeを更新
  const handlePlanChange = (planCode: string) => {
    const plan = availablePlans.find(p => p.code === planCode);
    const licenseType = mapPlanToLicenseType(planCode);
    setFormData({
      ...formData,
      plan_code: planCode,
      max_devices: plan?.max_devices || 1,
      license_type: licenseType
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('クリップボードにコピーしました');
  };

  const getLicenseStatusChip = (user: User) => {
    if (user.role === 'admin') {
      return (
        <Chip
          icon={<AdminPanelSettings />}
          label="管理者"
          color="info"
          size="small"
        />
      );
    }

    if (!user.is_active) {
      return (
        <Chip
          icon={<Cancel />}
          label="無効"
          color="error"
          size="small"
        />
      );
    }

    // マルチプロダクト対応: productsを優先チェック
    if (user.products && user.products.length > 0) {
      // 有効なプロダクトがあるかチェック
      const hasActiveProduct = user.products.some(p => p.is_active);
      if (hasActiveProduct) {
        // 最も早い有効期限を取得
        const validProducts = user.products.filter(p => p.is_active && p.subscription_ends_at);
        if (validProducts.length > 0) {
          const earliestExpiry = validProducts
            .map(p => p.subscription_ends_at)
            .sort()[0];
          const daysLeft = getDaysUntilExpiry(earliestExpiry ?? null);
          const label =
            daysLeft !== null && daysLeft > 0
              ? `有効（残り${daysLeft}日）`
              : '有効';
          return (
            <Chip
              icon={<CheckCircle />}
              label={label}
              color="success"
              size="small"
            />
          );
        }
        return (
          <Chip
            icon={<CheckCircle />}
            label="有効"
            color="success"
            size="small"
          />
        );
      }
    }

    // レガシー: user-levelの有効性チェック
    if (user.is_license_valid) {
      const daysLeft = getDaysUntilExpiry(user.subscription_ends_at);
      const label =
        daysLeft !== null && daysLeft > 0
          ? `有効（残り${daysLeft}日）`
          : '有効';

      return (
        <Chip
          icon={<CheckCircle />}
          label={label}
          color="success"
          size="small"
        />
      );
    }

    return (
      <Chip
        icon={<Cancel />}
        label="期限切れ"
        color="error"
        size="small"
      />
    );
  };

  // 1年後の日付を取得
  const getOneYearLater = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().slice(0, 16);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            ユーザー管理
          </Typography>
          <Typography variant="body1" color="text.secondary">
            ユーザーの作成、編集、削除を行います
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={openCreateDialog}
        >
          新規ユーザー作成
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />

      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>メールアドレス</TableCell>
                  <TableCell>氏名</TableCell>
                  <TableCell>契約プロダクト</TableCell>
                  <TableCell>有効期限</TableCell>
                  <TableCell>状態</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.full_name || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {/* 管理者チップ */}
                        {user.role === 'admin' && (
                          <Chip label="管理者" color="info" size="small" />
                        )}
                        {/* プロダクト一覧 */}
                        {user.products && user.products.length > 0 ? (
                          user.products.map((product, idx) => (
                            <Tooltip
                              key={idx}
                              title={
                                <Box>
                                  <Typography variant="caption" display="block">プラン: {product.plan_code}</Typography>
                                  <Typography variant="caption" display="block">デバイス: {product.max_devices}台</Typography>
                                  {product.license_token && (
                                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                                      Token: {product.license_token.slice(0, 16)}...
                                    </Typography>
                                  )}
                                </Box>
                              }
                            >
                              <Chip
                                label={product.product_name}
                                color={product.is_active ? 'primary' : 'default'}
                                size="small"
                                onClick={() => product.license_token && copyToClipboard(product.license_token)}
                              />
                            </Tooltip>
                          ))
                        ) : user.role !== 'admin' && (
                          <Chip label="未契約" color="default" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.role === 'admin'
                        ? '-'
                        : user.products && user.products.length > 0
                        ? formatDateOnly(user.products[0]?.subscription_ends_at ?? null)
                        : formatDateOnly(user.subscription_ends_at)}
                    </TableCell>
                    <TableCell>{getLicenseStatusChip(user)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="編集">
                        <IconButton size="small" color="primary" onClick={() => openEditDialog(user)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="削除">
                        <IconButton size="small" color="error" onClick={() => openDeleteDialog(user)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ユーザー作成ダイアログ */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>新規ユーザー作成</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Step 1: 役割選択 */}
            <FormControl fullWidth>
              <InputLabel>役割</InputLabel>
              <Select
                value={formData.role}
                label="役割"
                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              >
                <MenuItem value="user">一般ユーザー</MenuItem>
                <MenuItem value="admin">管理者</MenuItem>
              </Select>
            </FormControl>

            {/* Step 2: プロダクト選択（一般ユーザーのみ） */}
            {formData.role === 'user' && (
              <FormControl fullWidth required>
                <InputLabel>プロダクト</InputLabel>
                <Select
                  value={formData.product_code}
                  label="プロダクト"
                  onChange={(e) => handleProductChange(e.target.value)}
                >
                  {products.map((product) => (
                    <MenuItem key={product.code} value={product.code}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Step 3: プラン選択（プランがあるプロダクトのみ） */}
            {formData.role === 'user' && formData.product_code && availablePlans.length > 0 && (
              <FormControl fullWidth required>
                <InputLabel>プラン</InputLabel>
                <Select
                  value={formData.plan_code}
                  label="プラン"
                  onChange={(e) => handlePlanChange(e.target.value)}
                >
                  {availablePlans.map((plan) => (
                    <MenuItem key={plan.code} value={plan.code}>
                      {plan.name} - ¥{plan.price.toLocaleString()} （最大{plan.max_devices}台）
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Step 4: 基本情報（プロダクト選択後、または管理者の場合） */}
            {(formData.role === 'admin' || formData.product_code) && (
              <>
                <TextField
                  label="メールアドレス"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                <TextField
                  label="氏名"
                  fullWidth
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                />
                <TextField
                  label="パスワード（任意）"
                  type="password"
                  fullWidth
                  helperText="空欄の場合は自動生成"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </>
            )}

            {/* Step 5: プロダクト固有設定（一般ユーザーのみ） */}
            {formData.role === 'user' && formData.product_code && (
              <>
                <TextField
                  label="最大デバイス数"
                  type="number"
                  fullWidth
                  value={formData.max_devices}
                  onChange={(e) => setFormData({ ...formData, max_devices: parseInt(e.target.value) || 1 })}
                  InputProps={{
                    inputProps: { min: 1, max: 10 }
                  }}
                  helperText={availablePlans.length > 0 ? "プランに応じて自動設定されます" : "このプロダクトで使用可能なデバイス数"}
                />
                <TextField
                  label="有効期限"
                  type="datetime-local"
                  fullWidth
                  value={formData.subscription_ends_at || getOneYearLater()}
                  onChange={(e) => setFormData({ ...formData, subscription_ends_at: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="通常は購入日から1年"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>キャンセル</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={
              !formData.email ||
              (formData.password.length > 0 && formData.password.length < 8) ||
              (formData.role === 'user' && !formData.product_code) ||
              (formData.role === 'user' && availablePlans.length > 0 && !formData.plan_code)
            }
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* ユーザー編集ダイアログ */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザー編集</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="メールアドレス"
              fullWidth
              disabled
              value={formData.email}
            />
            <TextField
              label="氏名"
              fullWidth
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>役割</InputLabel>
              <Select
                value={formData.role}
                label="役割"
                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              >
                <MenuItem value="user">一般ユーザー</MenuItem>
                <MenuItem value="admin">管理者</MenuItem>
              </Select>
            </FormControl>

            {/* Gambit用フィールド（一般ユーザーのみ表示） */}
            {formData.role === 'user' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>ライセンスタイプ</InputLabel>
                  <Select
                    value={formData.license_type}
                    label="ライセンスタイプ"
                    onChange={(e) => setFormData({ ...formData, license_type: e.target.value as LicenseType })}
                  >
                    <MenuItem value="BASIC">Basic（Phase 1-4）</MenuItem>
                    <MenuItem value="PRO">Pro（Phase 1-11 + LP Generator）</MenuItem>
                    <MenuItem value="ENTERPRISE">Enterprise（複数デバイス対応）</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="最大デバイス数"
                  type="number"
                  fullWidth
                  value={formData.max_devices}
                  onChange={(e) => setFormData({ ...formData, max_devices: parseInt(e.target.value) || 1 })}
                  InputProps={{
                    inputProps: { min: 1, max: 10 }
                  }}
                />
                <TextField
                  label="有効期限"
                  type="datetime-local"
                  fullWidth
                  value={formData.subscription_ends_at ? formData.subscription_ends_at.slice(0, 16) : ''}
                  onChange={(e) => setFormData({ ...formData, subscription_ends_at: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />

                {/* ライセンストークン表示（編集時のみ） */}
                {selectedUser?.license_token && (
                  <TextField
                    label="ライセンストークン"
                    fullWidth
                    disabled
                    value={selectedUser.license_token}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="コピー">
                            <IconButton onClick={() => copyToClipboard(selectedUser.license_token!)}>
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleUpdateUser} variant="contained">
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* ユーザー削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>ユーザー削除確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当に以下のユーザーを削除しますか？
          </Typography>
          {selectedUser && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2"><strong>メールアドレス:</strong> {selectedUser.email}</Typography>
              <Typography variant="body2"><strong>氏名:</strong> {selectedUser.full_name || '-'}</Typography>
              <Typography variant="body2"><strong>プラン:</strong> {selectedUser.role === 'admin' ? '管理者' : LICENSE_TYPE_LABELS[selectedUser.license_type]}</Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            この操作は取り消せません。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
